import GridLayout, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import SharedTile from './SharedTile.jsx';
import { COLS, ROW_HEIGHT, GRID_MARGIN, mobileTileHeight, orderedItems, stackGroups, keepsRow, gridLayout } from '../lib/tileLayout.js';

// A shared report, laid out the way its dashboard is laid out. The public page
// (/r/:token) and the owner's preview both render THIS, so the preview is the
// article itself rather than an impression of it.
//
// Desktop is the same 24-column grid the dashboard uses; vertical compaction
// closes the gaps left by tiles this link doesn't share. On a phone the grid is
// dropped for a single column in reading order (down the rows, then across),
// keeping a strip of small KPI tiles together — squeezed grid cells are
// unreadable at 375px.
//
// With `editable`, tiles drag and resize and `onLayoutChange` receives the whole
// arrangement — that's the sender rearranging their own copy. Editing is desktop
// only: the phone view is a consequence of the desktop order, not its own layout.
export default function SharedReportGrid({
  tiles = [],
  carousels = [],
  load,
  refreshKey = 0,
  onFetched,
  isMobile = false,
  editable = false,
  onLayoutChange,
}) {
  const tile = (t) => <SharedTile key={t.id} tile={t} load={load} refreshKey={refreshKey} onFetched={onFetched} />;

  // A section (carousel) as the reader sees it: its title, then its tiles.
  const section = (c) => (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md, 14px)', boxShadow: 'var(--shadow-sm)', height: '100%', overflow: 'auto', padding: 12 }}>
      {c.title && <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, textAlign: c.titleAlign || 'left' }}>{c.title}</h2>}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, alignItems: 'stretch' }}>
        {(c.tiles || []).map((t) => <div key={t.id} style={{ minHeight: mobileTileHeight(t) || 120 }}>{tile(t)}</div>)}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {stackGroups(orderedItems(tiles, carousels)).map((g, gi) => {
          if (g.type === 'carousel') return <div key={g.it.el.id}>{section(g.it.el)}</div>;
          if (keepsRow(g)) {
            const rowH = Math.max(0, ...g.items.map((it) => mobileTileHeight(it.el) || 0));
            return (
              <div key={`row-${gi}`} style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
                {g.items.map((it) => (
                  <div key={it.el.id} style={{ flex: `${it.w} 1 0`, minWidth: 80, height: rowH || undefined }}>{tile(it.el)}</div>
                ))}
              </div>
            );
          }
          return (
            <div key={`stack-${gi}`} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {g.items.map((it) => <div key={it.el.id} style={{ height: mobileTileHeight(it.el) }}>{tile(it.el)}</div>)}
            </div>
          );
        })}
      </div>
    );
  }

  const handleChange = (next) => {
    if (!onLayoutChange) return;
    const map = {};
    for (const item of next) map[item.i] = { x: item.x, y: item.y, w: item.w, h: item.h };
    onLayoutChange(map);
  };

  return (
    <Grid
      className="layout"
      layout={gridLayout(tiles, carousels)}
      cols={COLS}
      rowHeight={ROW_HEIGHT}
      margin={GRID_MARGIN}
      isDraggable={!!editable}
      isResizable={!!editable}
      onDragStop={handleChange}
      onResizeStop={handleChange}
      compactType="vertical"
      preventCollision={false}
    >
      {tiles.map((t) => <div key={t.id} style={editable ? { cursor: 'move' } : undefined}>{tile(t)}</div>)}
      {carousels.map((c) => <div key={c.id} style={editable ? { cursor: 'move' } : undefined}>{section(c)}</div>)}
    </Grid>
  );
}

const Grid = WidthProvider(GridLayout);
